<script setup lang="ts">
import { reactive } from 'vue'
import { z } from 'zod'
import countries from '~/assets/json/dist.countries.json'
import states from '~/assets/json/dist.states.json'
import type { Country, State } from '~/types/register'
import { createPasswordSchema } from '~/utils/password'
import { createPhoneSchema } from '~/utils/phone'

const props = withDefaults(defineProps<{
  /**
   * Drop the icon, the heading and the subtitle.
   *
   * Inside the commit sheet those are the sheet's job, and repeating them cost
   * roughly 120px of a phone's screen before the first field — on the form that
   * most needs the room. The social callback still renders this standalone on
   * the auth layout, where the heading is the only thing naming the page, so
   * this is a prop rather than a deletion.
   */
  chromeLess?: boolean
  /**
   * The social hand-off this registration completes.
   *
   * Optional, because the same three questions — a password, who you are,
   * where to invoice you — are asked by the PUBLIC configurator's identity
   * step, which has no social ticket. It is passed straight back out in the
   * emitted payload, so an absent one costs the caller nothing and saves a
   * third copy of a nine-field billing address in this codebase.
   */
  ticketId?: string
  email: string
  firstName?: string
  lastName?: string
  loading?: boolean
  error?: string
  /** Override the heading when this is not the social callback. */
  title?: string
  subtitle?: string
}>(), { chromeLess: false })

const emit = defineEmits<{
  (e: 'submit', payload: Record<string, unknown>): void
  /**
   * Where this wizard has got to.
   *
   * Reported upwards rather than drawn here, because inside the commit sheet
   * the rail belongs to the sheet: two step components on one screen is two
   * answers to "where am I". The sheet folds this into the chip the customer
   * is standing on, so "how much more of this form is there?" — the question
   * that loses people on a nine-field billing address — still has an answer.
   */
  (e: 'stepChange', progress: { index: number, total: number }): void
}>()

const { t: $t } = useI18n()
// The CEP lookup is the console's own Nitro route: through the resolver, or
// the native app is answered by its own bundle (see `resolveNitroUrl`).
const { nitroFetch } = useMwBff()

// ── Wizard ────────────────────────────────────────────────────────────────
type Step = 'password' | 'profile' | 'address'
const steps: { key: Step; label: string }[] = [
  { key: 'password', label: 'checkout.socialRegister.stepPassword' },
  { key: 'profile', label: 'page.register.stepProfile' },
  { key: 'address', label: 'page.register.stepAddress' },
]
const step = ref<Step>('password')
const currentIndex = computed(() => steps.findIndex(s => s.key === step.value))

watch(
  currentIndex,
  index => emit('stepChange', { index: index + 1, total: steps.length }),
  { immediate: true },
)

const StateSchema = z.object({ name: z.string(), code: z.string() })

const passwordSchema = z.object({
  password: createPasswordSchema($t),
  confirmPassword: z.string().min(1),
}).refine(d => d.password === d.confirmPassword, {
  path: ['confirmPassword'],
  message: $t('auth.password.mismatch'),
})

const profileSchema = z.object({
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  company: z.string().optional(),
})

const addressSchema = z.object({
  country: z.object({ name: z.string(), countryCode: z.string() }).refine(
    data => data.name.length > 0,
    { message: $t('page.register.countryRequired') },
  ),
  state: z.union([z.string().min(2), StateSchema]),
  city: z.string().min(2),
  zip: z.string().min(4),
  address: z.string().min(5),
  phone: createPhoneSchema($t),
})

const state = reactive({
  password: '',
  confirmPassword: '',
  firstName: props.firstName ?? '',
  lastName: props.lastName ?? '',
  company: '',
  address: '',
  city: '',
  state: undefined as unknown as State | string,
  zip: '',
  country: undefined as unknown as Country,
  phone: '',
})

const stateInputType = computed(() => {
  if (state.country == undefined) return 'text'
  return (states as Record<string, State[]>)[state.country.countryCode]?.length > 0 ? 'select' : 'text'
})
const statesComputed = computed(() => (states as Record<string, State[]>)[state.country.countryCode])

watch(() => state.country, (newCountry, oldCountry) => {
  if (newCountry?.name !== oldCountry?.name) {
    state.state = undefined as unknown as State | string
  }
})

onMounted(() => {
  const language = navigator?.language?.split('-')
  if (language && language.length > 1) {
    const browserCountry = (countries as Country[]).find(c => c.countryCode === language[1])
    if (browserCountry) state.country = browserCountry
  }
})

/**
 * Re-check the fields WE wrote.
 *
 * `UForm` only re-validates a field when that field's own control emits
 * `input` / `blur` / `change`; a parent-driven `v-model` write emits nothing
 * to the form bus. So anything filled in FOR the customer keeps whatever error
 * it had before — "Belo Horizonte", in red, under "must contain at least 2
 * characters", beside a postcode that had just answered correctly. Verified in
 * the real commit sheet: three of the five address fields read as rejected the
 * instant the lookup succeeded.
 *
 * Scoped to the names passed, because `validate({ name })` REPLACES only those
 * errors and keeps the rest — so this can never clear a complaint about a field
 * the customer still has to answer. `silent` so a field that is genuinely still
 * wrong returns false instead of throwing out of a lookup.
 */
const addressFormRef = ref<{ validate: (opts: Record<string, unknown>) => Promise<unknown> } | null>(null)

async function revalidateFilled(...names: string[]) {
  await nextTick()
  // Swallows its own failure. This runs INSIDE `lookupCep`'s try, so a throw
  // here would land in that catch and report "we couldn't find that postcode"
  // over an address it had just filled in correctly — and a stale error left
  // on screen is a far smaller problem than a wrong answer about the lookup.
  // It is also what makes the `void` call below safe.
  try {
    await addressFormRef.value?.validate({ name: names, silent: true })
  }
  catch { /* clearing an error is never worth failing anything for */ }
}

/**
 * Adopt a country the BROWSER filled in, from the paired native `<select>`.
 *
 * Without this the autofilled value lives only on that element and the model
 * keeps whatever the picker last had — which is the half of the documented gap
 * that made autofill pointless even where it worked.
 */
function onCountryAutofill(code: string) {
  const match = (countries as Country[]).find(c => c.countryCode === code)
  if (match) {
    state.country = match
    // Written for the customer, so the form heard nothing — same rule as the
    // postcode fill. Only `country`, never `state`: the watcher above clears
    // the state when the country moves, and complaining about a field nobody
    // has reached yet is the opposite of the problem being fixed.
    void revalidateFilled('country')
  }
}

/**
 * Fill the address from the postcode.
 *
 * WHMCS refuses an account without country, state, city, postcode and street,
 * so this form cannot be made shorter — but it can be made mostly answered.
 * Eight digits instead of five typed fields, on the highest-friction screen in
 * the funnel.
 *
 * Everything it writes stays editable, nothing it writes is required to be
 * right, and a lookup that fails says so quietly and changes nothing. The
 * proxy is `server/api/address/cep/[cep].ts`.
 */
const cepState = ref<'idle' | 'looking' | 'filled' | 'notFound'>('idle')
let lastCepLookup = ''

const cepDigits = computed(() => state.zip.replace(/\D/g, ''))

/** Only Brazil has a CEP; anywhere else this must not fire at all. */
const cepLookupApplies = computed(() => state.country?.countryCode === 'BR')

async function lookupCep() {
  if (!cepLookupApplies.value) return
  const cep = cepDigits.value
  if (cep.length !== 8 || cep === lastCepLookup) return
  lastCepLookup = cep
  cepState.value = 'looking'
  try {
    const res = await nitroFetch<{
      found: boolean
      street?: string
      city?: string
      state?: string
    }>(`/api/address/cep/${cep}`)
    if (!res?.found) {
      cepState.value = 'notFound'
      return
    }
    // Overwrite rather than only filling blanks: the customer has just changed
    // the postcode, so a city left over from the previous one is wrong, and
    // silently keeping it is how an invoice goes to the wrong place.
    if (res.street) state.address = res.street
    if (res.city) state.city = res.city
    if (res.state && stateInputType.value === 'select') {
      const match = statesComputed.value?.find(s => s.code === res.state)
      if (match) state.state = match
    }
    else if (res.state) {
      state.state = res.state
    }
    cepState.value = 'filled'
    await revalidateFilled('address', 'city', 'state')
  }
  catch {
    // Never a blocker: the fields are all still there to type into.
    cepState.value = 'notFound'
  }
}

/** Typing a new postcode invalidates whatever the last one said. */
watch(cepDigits, (digits) => {
  if (digits.length !== 8) cepState.value = 'idle'
  else void lookupCep()
})

/**
 * The postcode may be answered BEFORE the country is.
 *
 * The lookup is gated on Brazil, and the only trigger is the postcode itself —
 * so a customer who filled the postcode first (or whose browser autofilled the
 * whole address at once) and then chose Brasil got no lookup at all, silently.
 * Putting the postcode second makes that less likely, not impossible.
 *
 * Leaving Brazil also forgets the last lookup: without that, coming back to it
 * with the same postcode still on screen is a no-op, and the state the country
 * watcher just cleared would stay empty.
 */
watch(cepLookupApplies, (applies) => {
  if (!applies) { lastCepLookup = ''; cepState.value = 'idle'; return }
  if (cepDigits.value.length === 8) void lookupCep()
})

function onPasswordSubmit() { step.value = 'profile' }
function backToPassword() { step.value = 'password' }
function onProfileSubmit() { step.value = 'address' }
function backToProfile() { step.value = 'profile' }

function onSubmitFinal() {
  emit('submit', {
    ticketId: props.ticketId,
    password: state.password,
    firstname: state.firstName,
    lastname: state.lastName,
    companyname: state.company || undefined,
    address1: state.address,
    city: state.city,
    state: typeof state.state === 'string' ? state.state : state.state.code,
    postcode: state.zip,
    country: state.country.countryCode,
    phonenumber: state.phone,
  })
}
</script>

<template>
  <div>
    <div v-if="!chromeLess" class="text-center mb-6">
      <div class="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-100 dark:bg-primary-900/30 mb-3">
        <UIcon name="i-lucide-user-plus" class="w-6 h-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h2 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
        {{ title ?? $t('checkout.socialRegister.title') }}
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        {{ subtitle ?? $t('checkout.socialRegister.subtitle') }}
      </p>
    </div>

    <!-- Progress stepper.
         Hidden inside the commit sheet, which carries its own — and two step
         rails stacked on one screen is two answers to "where am I". The sheet's
         three steps are the ones that describe the PURCHASE; password, profile
         and address are how one of them is filled in, and the Back/Continue
         pair is enough to say so. Still shown on the social callback page,
         where this component stands alone and the rail is the only chrome. -->
    <div v-if="!chromeLess" class="flex items-center mb-6">
      <template v-for="(s, i) in steps" :key="s.key">
        <div class="flex items-center gap-2">
          <div
            class="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
            :class="currentIndex >= i
              ? 'bg-primary-500 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'"
          >
            <UIcon v-if="currentIndex > i" name="i-lucide-check" class="w-4 h-4" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span
            class="text-xs font-medium hidden sm:inline"
            :class="currentIndex >= i ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'"
          >{{ $t(s.label) }}</span>
        </div>
        <div v-if="i < steps.length - 1" class="flex-1 h-px mx-2" :class="currentIndex > i ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'" />
      </template>
    </div>

    <UAlert
      v-if="error"
      color="error"
      icon="i-heroicons-exclamation-triangle"
      class="mb-4 rounded-lg"
      :title="error"
    />

    <!-- Step 1: Password (email locked from the verified provider) -->
    <UForm v-if="step === 'password'" :schema="passwordSchema" :state="state" class="space-y-4" @submit="onPasswordSubmit">
      <UFormField :label="$t('page.register.email')">
        <UInput :model-value="email" size="xl" icon="i-heroicons-envelope" disabled class="w-full" />
      </UFormField>

      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ $t('checkout.socialRegister.passwordWhy') }}
      </p>

      <AuthPasswordCreateFields
        v-model:password="state.password"
        v-model:confirm="state.confirmPassword"
        :disabled="loading"
      />

      <UButton type="submit" size="xl" block trailing-icon="i-heroicons-arrow-right-20-solid" class="shadow-lg hover:shadow-xl transition-all">
        {{ $t('page.register.next') }}
      </UButton>
    </UForm>

    <!-- Step 2: Profile -->
    <UForm v-else-if="step === 'profile'" :schema="profileSchema" :state="state" class="space-y-4" @submit="onProfileSubmit">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.name')" name="firstName">
          <UInput v-model="state.firstName" size="xl" icon="i-heroicons-user" :placeholder="$t('page.register.nameDescription')" autocomplete="given-name" class="w-full" />
        </UFormField>
        <UFormField :label="$t('page.register.surname')" name="lastName">
          <UInput v-model="state.lastName" size="xl" icon="i-heroicons-user" :placeholder="$t('page.register.surnameDescription')" autocomplete="family-name" class="w-full" />
        </UFormField>
      </div>

      <UFormField :label="`${$t('page.register.company')} (${$t('page.register.optional')})`" name="company">
        <UInput v-model="state.company" size="xl" icon="i-heroicons-building-office" :placeholder="$t('page.register.companyDescription')" autocomplete="organization" class="w-full" />
      </UFormField>

      <div class="flex gap-3 pt-2">
        <UButton variant="outline" size="xl" class="shrink-0" icon="i-heroicons-arrow-left-20-solid" @click="backToPassword">{{ $t('general.back') }}</UButton>
        <UButton type="submit" size="xl" class="flex-1 justify-center shadow-lg" trailing-icon="i-heroicons-arrow-right-20-solid">{{ $t('page.register.next') }}</UButton>
      </div>
    </UForm>

    <!-- Step 3: Address / billing -->
    <UForm ref="addressFormRef" v-else-if="step === 'address'" :schema="addressSchema" :state="state" class="space-y-4" @submit="onSubmitFinal">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.country')" name="country">
          <USelectMenu
            v-model="(state.country as any)"
            :items="countries"
            searchable
            :filter-fields="['name', 'countryCode']"
            label-key="name"
            size="xl"
            icon="i-heroicons-globe-alt"
            :placeholder="$t('page.register.countryDescription')"
            class="w-full"
            trailing
          />
          <!--
            A real control for the browser to autofill into.

            `USelectMenu` renders no form control a browser recognises, and its
            `v-model` is bound to the component rather than to any input — so a
            filled value was both unfillable AND, if it had been filled, thrown
            away. Autofill is an accessibility feature, not a convenience: a
            nine-field billing address is exactly what people with motor or
            cognitive impairments rely on it for, and this is the highest-stakes
            form in the funnel.

            It is `aria-hidden` with `tabindex="-1"` so assistive tech and the
            Tab order see ONE country control (the searchable one above), while
            the browser still has something autocomplete="country" can target.
            `onCountryAutofill` is what stops a filled value being discarded.
          -->
          <select
            class="sr-only"
            aria-hidden="true"
            tabindex="-1"
            autocomplete="country"
            :value="state.country?.countryCode ?? ''"
            @change="onCountryAutofill(($event.target as HTMLSelectElement).value)"
          >
            <option value="" />
            <option v-for="c in (countries as Country[])" :key="c.countryCode" :value="c.countryCode">
              {{ c.name }}
            </option>
          </select>
        </UFormField>
        <!-- The postcode sits SECOND, immediately after the country, because
             it fills the three fields below it. In the old order — state,
             city, postcode — a Brazilian customer typed a city and a state
             and then watched the lookup overwrite both, which is worse than
             not filling anything: it is work thrown away in front of them.
             Country still has to come first, since the lookup is gated on
             it. -->
        <UFormField :label="$t('page.register.zip')" name="zip">
          <!-- `inputmode` is what puts a numeric pad under a thumb instead of
               a full QWERTY keyboard. On the two most numeric fields of the
               longest form in the funnel it is worth more than it looks. -->
          <UInput
            v-model="state.zip"
            size="xl"
            inputmode="numeric"
            icon="i-heroicons-hashtag"
            :placeholder="$t('page.register.zipDescription')"
            autocomplete="postal-code"
            class="w-full"
            @blur="lookupCep"
          />
          <!-- Announced, never silent — the same rule the checkout's automatic
               disk raise follows. A form that fills itself in without saying so
               reads as a form that has lost what you typed. -->
          <p
            v-if="cepLookupApplies && cepState !== 'idle'"
            role="status"
            class="mt-1 text-xs"
            :class="cepState === 'notFound' ? 'text-muted' : 'text-toned'"
          >
            <template v-if="cepState === 'looking'">{{ $t('page.register.zipLookingUp') }}</template>
            <template v-else-if="cepState === 'filled'">{{ $t('page.register.zipFilled') }}</template>
            <template v-else>{{ $t('page.register.zipNotFound') }}</template>
          </p>
        </UFormField>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <UFormField :label="$t('page.register.state')" name="state">
          <UInput
            v-if="stateInputType === 'text'"
            v-model="state.state as string"
            size="xl"
            icon="i-heroicons-map"
            :placeholder="$t('page.register.stateDescription')"
            autocomplete="address-level1"
            class="w-full"
          />
          <USelectMenu
            v-else
            v-model="(state.state as any)"
            :items="statesComputed"
            searchable
            :filter-fields="['name', 'code']"
            label-key="name"
            size="xl"
            icon="i-heroicons-map"
            :placeholder="$t('page.register.stateDescription')"
            class="w-full"
            trailing
          />
        </UFormField>
        <UFormField :label="$t('page.register.city')" name="city">
          <UInput v-model="state.city" size="xl" icon="i-heroicons-building-office-2" :placeholder="$t('page.register.cityDescription')" autocomplete="address-level2" class="w-full" />
        </UFormField>
      </div>

      <UFormField :label="$t('page.register.address')" name="address">
        <UInput v-model="state.address" size="xl" icon="i-heroicons-home" :placeholder="$t('page.register.addressDescription')" autocomplete="street-address" class="w-full" />
      </UFormField>

      <UFormField :label="$t('page.register.phone')" name="phone">
        <UInput v-model="state.phone" type="tel" inputmode="tel" size="xl" icon="i-heroicons-phone" :placeholder="$t('page.register.phoneDescription')" autocomplete="tel" class="w-full" />
      </UFormField>

      <!-- Back is sized to its own label, not to half the footer: an equal
           split squeezed the primary action onto two lines, and the two are
           not equal actions. -->
      <div class="flex gap-3 pt-2">
        <UButton variant="outline" size="xl" class="shrink-0" icon="i-heroicons-arrow-left-20-solid" :disabled="loading" @click="backToProfile">{{ $t('general.back') }}</UButton>
        <UButton type="submit" size="xl" class="flex-1 justify-center shadow-lg" :loading="loading" trailing-icon="i-heroicons-arrow-right-20-solid">{{ $t('checkout.socialRegister.submit') }}</UButton>
      </div>
    </UForm>
  </div>
</template>
